import { api } from '../lib/api'

export type PatientRow = {
  id: string
  number_of_clinical_record: number | null
  name: string | null
  rut: string | null
  phone_number: string | null
  company_which_derives: string | null
  interviews_count: number
  active: boolean
  toggle_active_path: string
  record_path: string
}

export type PatientsIndex = {
  recordsTotal: number
  recordsFiltered: number
  patientsTotal: number
  patients: PatientRow[]
}

export type Patient = {
  id: string
  name: string | null
  rut: string | null
  email: string | null
  date_born: string | null
  domicile: string | null
  education: string | null
  marital_status: string | null
  number_of_childrens: number | null
  prevision: string | null
  phone_number: string | null
  company_which_derives: string | null
  health_center_id: number
  sex: string | null
  commune: string | null
  representative_name: string | null
  representative_phone: string | null
  emergency_number: string | null
  occupation: string | null
  active: boolean
  age: number | null
  number_of_clinical_record: number | null
  custom_form_html: string
  custom_fields: Record<string, unknown>
  custom_info: Record<string, string>
  derivations: string[]
  record_path?: string
  notice?: string
}

export type PatientAttributes = {
  rut?: string
  name?: string
  date_born?: string
  education?: string
  marital_status?: string
  number_of_childrens?: string
  prevision?: string
  occupation?: string
  sex?: string
  phone_number?: string
  email?: string
  company_which_derives?: string
  domicile?: string
  commune?: string
  representative_name?: string
  representative_phone?: string
  emergency_number?: string
  health_center_id?: number
}

export function fetchPatients(
  healthCenterId: number,
  options: {
    start: number
    length: number
    search: string
    archivedOnly: boolean
    orderColumn: number | null
    orderDir: 'asc' | 'desc'
    mode: string | null
  },
): Promise<PatientsIndex> {
  const params = new URLSearchParams()
  params.set('start', String(options.start))
  params.set('length', String(options.length))
  if (options.search) {
    params.set('search[value]', options.search)
  }
  params.set('archived_patients_only', String(options.archivedOnly))
  if (options.mode) {
    params.set('mode', options.mode)
  }
  if (options.orderColumn !== null) {
    params.set('order[0][column]', String(options.orderColumn))
    params.set('order[0][dir]', options.orderDir)
  }
  return api.get<PatientsIndex>(`/health_centers/${healthCenterId}/patients.json?${params.toString()}`)
}

export function fetchPatient(healthCenterId: number, patientId: string): Promise<Patient> {
  return api.get<Patient>(`/health_centers/${healthCenterId}/patients/${patientId}.json`)
}

export function updatePatient(
  healthCenterId: number,
  patientId: string,
  mode: string | null,
  patient: PatientAttributes,
  customInfo: Record<string, string>,
): Promise<Patient> {
  const modeQuery = mode ? `?mode=${mode}` : ''
  return api.patch<Patient>(`/health_centers/${healthCenterId}/patients/${patientId}.json${modeQuery}`, {
    patient,
    patient_custom_info: customInfo,
  })
}

export function createPatient(
  healthCenterId: number,
  mode: string | null,
  patient: PatientAttributes,
  customInfo: Record<string, string>,
): Promise<Patient> {
  const modeQuery = mode ? `?mode=${mode}` : ''
  return api.post<Patient>(`/health_centers/${healthCenterId}/patients.json${modeQuery}`, {
    patient,
    patient_custom_info: customInfo,
  })
}

export function findOrCreatePatient(healthCenterId: number, rut: string, mode: string | null): Promise<{ redirect: string }> {
  return api.post<{ redirect: string }>('/patients/find_or_create', {
    new_patient_rut: rut,
    health_center_id: healthCenterId,
    mode: mode ?? '',
  })
}

export function toggleActivePatient(togglePath: string): Promise<{ active: boolean; notice: string }> {
  return api.post<{ active: boolean; notice: string }>(togglePath)
}
